import { ReactComponent as Pen } from 'assets/img/icons/v2/pen.svg';
import BssoPermissions from 'components/LM/Bsso/Permissions';
import ModalDisplay from "components/LM/common/ModalDisplay";
import ModalSearchItem from 'components/LM/SocialMediaProfile/ModalSearchItem';
import VoiceMedia from 'components/LM/Voice/Media';
import VoiceProfile from 'components/LM/Voice/Profile';
import VoiceUrl from 'components/LM/Voice/Url';
import { useEffect, useReducer, useState } from "react";
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Row from 'react-bootstrap/Row';
import { Plus, Trash2 } from 'react-feather';

import { Loader } from "@launchmetrics/lm-ui";
import 'components/LM/Voice/Relations.scss';
import K2Api from "lib/ajax/K2Api";

const K2_API = new K2Api();
const SUPPORTED_SOCIALMEDIA_PROVIDERS = new Set(['facebook', 'instagram', 'tiktok', 'twitter', 'youtube', 'weibo', 'douyin', 'xiaohongshu', 'bilibili', 'wechat_public']);

function Relations(props) {
	const [loading, setLoading] = useState(false);
	const { voiceId, voiceName, voiceType } = props;


	const [showRemoveModal, setShowRemoveModalState] = useState(false);

	const [state, setState] = useReducer(
		(currentState, newState) => ({ ...currentState, ...newState }),
		{
			profiles: flattenProfilesObject(props.profiles),
			medias: flattenProfilesObject(props.medias),
			crawling: flattenProfilesObject(props.crawling),
			urls: props.urls,
			editMode: props.editMode,
			saving: false,
			showModalProfileSearch: false,
			isModified: false,
			editType: false
		}
	);

	useEffect(() => {
        if (state.isModified) {
            onSave();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isModified]);

	const onToggleProfile = (type, id, enabled) => {
		const profile = state.profiles.find(p => p.id === id && type === p.type);
		profile.enabled = !enabled;
		setState({ profiles: [...state.profiles] });
	};

	const onToggleMedia = (type, id, enabled) => {
		const media = state.medias.find(p => p.id === id && type === p.type);
		media.enabled = !enabled;
		setState({ medias: [...state.medias] });
	};

	const onToggleUrl = (id, enabled) => {
		const url = state.urls.find(p => p.id === id);
		url.enabled = !enabled;
		setState({ urls: [...state.urls] });
	};

	const onAddRelations = (type, relations) => {
		if (type === 'online' || type === 'print') {
			onAddMedias(type, relations);
		} else if (type === 'socialmedia' || SUPPORTED_SOCIALMEDIA_PROVIDERS.has(type)) {
			onAddProfiles(type, relations);
		} else if (type === 'other') {
			onAddUrls(type, relations);
		}
	};

	const onAddProfiles = (type, entities) => {
		const currentProfileIds = new Set(state.profiles.filter(p => p.type === type).map(p => p.id));
		const newProfiles = entities.filter(entity => !currentProfileIds.has(entity.id));
		if (!newProfiles.length) return;

		const profilesAdd = newProfiles.map(p => { return { ...p, new: true, type: p.profile_type ?? type } });
		setState({ profiles: [...state.profiles, ...profilesAdd], isModified: true  });
	}

	const onAddMedias = (type, entities) => {
		const currentMediaIds = new Set(state.medias.filter(p => p.type === type).map(m => m.id));
		const newMedias = entities.filter(entity => !currentMediaIds.has(entity.id));
		if (!newMedias.length) return;

		const mediasAdd = newMedias.map(m => { return { ...m, new: true, type } });
		setState({ medias: [...state.medias, ...mediasAdd], isModified: true });
	}

	const onAddUrls = (type, entities) => {
		const currentUrls = new Set(state.urls.map(m => m.id));
		const newUrls = entities.filter(entity => !currentUrls.has(entity.id));
		if (!newUrls.length) return;

		const urlsAdd = newUrls.map(m => { return { ...m, new: true, type } });
		setState({ urls: [...state.urls, ...urlsAdd], isModified: true });
	}

	const onCancel = () => {
		state.profiles
			.forEach(profile => {
				onToggleProfile(profile.type, profile.id, false);
			});
		state.medias
			.forEach(media => {
				onToggleMedia(media.type, media.id, false);
			});
		state.urls
			.forEach(url => {
				onToggleUrl(url.id, false);
			});
		setState({ editType: false })
	}

	const showLoader = () => {
		if (loading) {
			return <Loader styleProps={{ marginLeft: '10px'}}size={20} />;
		}
	}

	const removeButtonText = (
		<>
		  <Trash2 size="13" /> Remove Source {showLoader()}
		</>
	);

	const onSave = async () => {
		setLoading(true);
		setState({ saving: true });
		const savedProfiles = await saveItems(voiceId, state.profiles);
		const savedMedias = await saveItems(voiceId, state.medias);
		const savedUrls = await saveUrls(voiceId, state.urls);

		setState({
			editMode: true,
			profiles: [...savedProfiles],
			medias: [...savedMedias],
			urls: [...savedUrls],
			saving: false,
			isModified: false
		});
		setShowRemoveModalState(false);
		setLoading(false);
		props.onSourcesChanged?.();
	};

	return (
		<>
			<Card className="relations">
				<Card.Body>
					<Card.Title as="h2" className="d-flex justify-content-between align-items-center">
						<div>Sources</div>
						<div className="d-flex">
							{state.editMode &&
								<EditButtons
									onSave={onSave}
									onCancel={onCancel}
									loading={loading}
									showLoader={showLoader}
									state={state}
									setState={setState}
									showRemoveModal={showRemoveModal}
									removeButtonText={removeButtonText}
									setShowRemoveModalState={setShowRemoveModalState}
									voiceName={voiceName}
								/>
							}
						</div>
					</Card.Title>
					<hr/>
					<Row>
						<Col md="6" lg="6" xl="3">
							<h4 className="mb-3 mt-2">Social</h4>
							<ListGroup>
								<VoiceProfilesItems
									profiles={state.profiles}
									crawling={state.crawling}
									medias={[]}
									type="social"
									urls={[]}
									editMode={state.editMode}
									disabled={state.saving}
									editType={state.editType}
									onToggleProfile={onToggleProfile}
									onToggleMedia={onToggleMedia}
									onToggleUrl={onToggleUrl}
								/>
							</ListGroup>
						</Col>
						<Col md="6" lg="6" xl="3">
							<h4 className="mb-3 mt-2">Print</h4>
							<ListGroup>
								<VoiceProfilesItems
									profiles={[]}
									medias={state.medias}
									type="print"
									urls={[]}
									editMode={state.editMode}
									disabled={state.saving}
									editType={state.editType}
									onToggleProfile={onToggleProfile}
									onToggleMedia={onToggleMedia}
									onToggleUrl={onToggleUrl}
								/>
							</ListGroup>
						</Col>
						<Col md="6" lg="6" xl="3">
							<h4 className="mb-3 mt-2">Online</h4>
							<ListGroup>
								<VoiceProfilesItems
									profiles={[]}
									medias={state.medias}
									type="online"
									urls={[]}
									editMode={state.editMode}
									disabled={state.saving}
									editType={state.editType}
									onToggleProfile={onToggleProfile}
									onToggleMedia={onToggleMedia}
									onToggleUrl={onToggleUrl}
								/>
							</ListGroup>
						</Col>
						<Col md="6" lg="6" xl="3">
							<h4 className="mb-3 mt-2">Link</h4>
							<ListGroup>
								<VoiceProfilesItems
									profiles={[]}
									medias={[]}
									type="url"
									urls={state.urls}
									editMode={state.editMode}
									disabled={state.saving}
									editType={state.editType}
									onToggleProfile={onToggleProfile}
									onToggleMedia={onToggleMedia}
									onToggleUrl={onToggleUrl}
								/>
							</ListGroup>
						</Col>
					</Row>
				</Card.Body>
			</Card>

			<ModalSearchItem
				show={state.showModalProfileSearch}
				onClose={() => setState({ showModalProfileSearch: false })}
				onAdd={onAddRelations}
				voiceId={voiceId}
				voiceType={voiceType}
			/>
		</>
	);
}


function EditButtons(props) {
	const { disabled, loading, showLoader } = props;
	const { onSave , onCancel} = props;
	const { state , setState} = props;
	const { showRemoveModal, removeButtonText, setShowRemoveModalState, voiceName } = props;

	const totalDisabledCount = state.profiles.filter(profile => profile.enabled === false).length
                        + state.medias.filter(media => media.enabled === false).length
                        + state.urls.filter(url => url.enabled === false).length;

	return <>
		{!state.editType ? (
			<>
			<Button key={`btnEdit`} className="manage btn-sm ml-2 edit_sources_button" disabled={disabled || loading} variant="outline-dark custom-gray-border" onClick={() => setState({ editType: true })}>
				<Pen height="12" width="12" className="pen-icon"/> Edit Sources
			</Button>
			<BssoPermissions permissions={['edit']} behaviour="no_render">
				{state.editMode &&
				<Button key={`btnManage`} className="manage btn-sm ml-2" disabled={state.saving} onClick={() => setState({ showModalProfileSearch: true })}>
					<Plus size="15" className="plus-icon"/>
					Add Source {showLoader()}
				</Button>}
			</BssoPermissions>
			</>
		):(
			<>
			<Button key={`btnCancelEdit`} className="manage btn-sm ml-2" disabled={disabled || loading} variant="outline-dark custom-gray-border" onClick={onCancel}>
			End editing
			</Button>
			<Button key={`btnRemove`} className="manage btn-sm ml-2" disabled={disabled || loading || !totalDisabledCount} variant="danger" onClick={() => { setShowRemoveModalState(!showRemoveModal) }}>
				<Trash2 size="13" /> Remove <span className="font-weight-bold">{totalDisabledCount}</span> selected source(s) {showLoader()}
			</Button>
			<ModalDisplay
				isVisible={showRemoveModal}
				contentText={`Are you sure you want to remove the following sources from ${voiceName}?`}
				color="danger"
				confirmButtonText={removeButtonText}
				onCancel={() => setShowRemoveModalState(!showRemoveModal)}
				onConfirm={onSave}
				state={state}
			/>
			</>
		)}
	</>
}

// The objective of this function is to turn profiles object {twitter => [...], facebook => [...]} into simple list,
// the profile type is added to profile. This will make more easy all the profiles operation management
function flattenProfilesObject(entities) {
	return Object.entries(entities).flatMap(([type, entities]) => entities.map(p => { return { ...p, type } }));
}

function VoiceProfilesItems(props) {
	const { profiles, medias, urls, crawling, editMode, editType, type } = props;
	const { onToggleProfile, onToggleMedia, onToggleUrl } = props;

	profiles.sort((first, second) => {
		return first.type.localeCompare(second.type) || first.name.localeCompare(second.name);
	});

	const voiceProfilesItems = [];

	if (type === 'social') {

		const profilesList = profiles.map(item => {
			const crawlingItem = crawling.find(
			  secondItem => secondItem.id === item.id && secondItem.type === item.type
			);

			if (crawlingItem) {
			  item.crawling = {
				error_counter: crawlingItem.error_counter,
				enabled: crawlingItem.enabled
			  };
			}
			return item;
		});

		profilesList.forEach(profile => {
			const voiceProfile = (<VoiceProfile
				type={profile.type}
				editType={editType}
				profile={profile}
				checked={false}
				editMode={editMode}
				onToggle={v => onToggleProfile(profile.type, profile.id, v)} />);

			let listItemClass = itemClass(profile, editMode);

			voiceProfilesItems.push(
				<ListGroup.Item className={listItemClass} key={`${profile.type}_${profile.id}`}>{voiceProfile}</ListGroup.Item>
			);

		});
	}

	if (type === 'print') {
		medias.forEach(media => {
			if (media.type === 'print') {
				const voiceMedia = (<VoiceMedia
					media={media}
					type={media.type}
					editType={editType}
					checked={false}
					editMode={editMode}
					onToggle={v => onToggleMedia(media.type, media.id, v)} />);

				const listItemClass = itemClass(media, editMode);

				voiceProfilesItems.push(
					<ListGroup.Item className={listItemClass} key={`${media.type}_${media.id}`}>{voiceMedia}</ListGroup.Item>
				);
			}
		});
	}

	if (type === 'online') {
		medias.forEach(media => {
			if (media.type === 'online') {
				const voiceMedia = (<VoiceMedia
					media={media}
					type={media.type}
					editType={editType}
					checked={false}
					editMode={editMode}
					onToggle={v => onToggleMedia(media.type, media.id, v)} />);

				const listItemClass = itemClass(media, editMode);

				voiceProfilesItems.push(
					<ListGroup.Item className={listItemClass} key={`${media.type}_${media.id}`}>{voiceMedia}</ListGroup.Item>
				);
			}
		});
	}

	if (type === 'url') {
		urls.forEach(url => {
			const voiceUrl = (<VoiceUrl
				url={url}
				type={url.type}
				editType={editType}
				checked={false}
				editMode={editMode}
				onToggle={v => onToggleUrl(url.id, v)} />
			);

			let listItemClass = itemClass(url, editMode);
			voiceProfilesItems.push(
				<ListGroup.Item className={listItemClass} key={`${url.type}_${url.id}`}>{voiceUrl}</ListGroup.Item>
			);
		});
	}

	return voiceProfilesItems;
}

function itemClass(item, editMode) {

	let listItemClass;
	if (editMode && item.new && item.enabled !== false) {
		listItemClass = "new-item";
	} else if (editMode && item.enabled === false) {
		listItemClass = "delete-item";
		//alert(1)
	}

	return listItemClass;
}

async function saveItems(voiceId, items) {

	const promises = [];
	items.forEach((item) => {
		const isEnabled = item.enabled !== false;
		const isNew = item.new;

		if (isNew && isEnabled) {
			promises.push(addProfile(voiceId, item));
		} else if (!isNew && !isEnabled) {
			promises.push(deleteProfile(voiceId, item));
		}
	});

	await Promise.allSettled(promises);

	return items.filter(p => p.error || p.enabled !== false);
}

async function saveUrls(voiceId, urls) {
	const promises = [];
	urls.forEach((url) => {
		const isEnabled = url.enabled !== false;
		const isNew = url.new;

		if (isNew && isEnabled) {
			promises.push(addUrl(voiceId, url));
		} else if (!isNew && !isEnabled) {
			promises.push(deleteUrl(voiceId, url));
		}
	});

	await Promise.allSettled(promises);

	return urls.filter(p => p.error || p.enabled !== false);
}

function addProfile(voiceId, profile) {
	const promiseAdd = K2_API.put(`/voices/${voiceId}/socialmedia/${profile.type}/${profile.id}`);
	promiseAdd.then(() => {
		delete profile.new;
		delete profile.error;
		delete profile.warning;
	}).catch(error => {
		if (error.response.status === 409) { // Already exists, we treat as ok too
			delete profile.new;
			delete profile.error;
		} else if (error.response.status === 410) { // Taken by another voice
			const takenByVoice = error.response.data;
			const voiceLink = <a className="text-danger" href={`/voices/${takenByVoice}`} target="_blank" rel="noopener noreferrer">{takenByVoice}</a>;
			profile['error'] = <>The profile is taken by voice with id: {voiceLink}</>;
		} else if (error.response.status === 418) { // Business restrictions applied on save
			profile['error'] = error.response.data.errors.join("/");
		} else if (error.response.status === 423) {
			profile['error'] = <>Cannot add it because the voice is locked by the user: {error.response.data.user}</>
		} else {
			profile['error'] = error.message;
		}
	});

	return promiseAdd;
}

function deleteProfile(voiceId, profile) {
	const promiseDelete = K2_API.delete(`/voices/${voiceId}/socialmedia/${profile.type}/${profile.id}`);

	promiseDelete.catch(error => {
		if (error.response.status === 423) { // Business restrictions applied on save
			profile['error'] = <>Cannot delete it because the voice is locked by the user: {error.response.data.user}</>
		} else if (error.reason.response.status !== 404) { // 404 means delete before, anything different should be considered as error
			profile['error'] = error.message;
		}
	});

	return promiseDelete;
}

function addUrl(voiceId, url) {

	const promiseAdd = K2_API.put(`/voices/${voiceId}/urls`, { url: url.url });
	promiseAdd.then((response) => {
		delete url.new;
		delete url.error;
		url.id = response.data.id;
		url.type = response.data.type;
	}).catch(error => {
		if (error.response && error.response.status === 409) {
			url['error'] = <>Cannot add the voice url because this url {error.response.data} is already linked to the current voice</>;
		} else if (error.response.status === 423) {
			url['error'] = <>Cannot add it because the voice is locked by the user: {error.response.data.user}</>
		} else if (error.response.status === 400) {
			url['error'] = <>Cannot add url: {error.response.data.errors.map(err => err.message).join(", ")}</>;
		} else {
			url['error'] = error.message;
		}
	});

	return promiseAdd;
}

function deleteUrl(voiceId, url) {
	const promiseDelete = K2_API.delete(`/voices/${voiceId}/urls/${url.id}`);

	promiseDelete.catch(error => {
		if (error.response.status === 423) { // Business restrictions applied on save
			url['error'] = <>Cannot delete it because the voice is locked by the user: {error.response.data.user}</>
		} else if (error.reason.response.status !== 404) { // 404 means delete before, anything different should be considered as error
			url['error'] = error.message;
		}
	});
}

export default Relations;
